import {
  Directive,
  ElementRef,
  Renderer2,
  inject,
  AfterViewInit,
  OnDestroy,
  HostListener,
  Input,
  HostBinding,
} from '@angular/core';

@Directive({
  selector: '[enterToFocusWithin]',
  standalone: true,
})
export class EnterToFocusWithinDirective implements AfterViewInit, OnDestroy {
  private readonly elRef = inject(ElementRef) as ElementRef<HTMLElement>;
  private readonly hostEl: HTMLElement = this.elRef.nativeElement;
  private readonly renderer = inject(Renderer2);

  private inside = false;
  private managed: Array<{ el: HTMLElement; orig?: string | null }> = [];
  private suppressFocusBounce = false;


  @Input() firstFocusSelector?: string;

  @HostBinding('class.card--locked') get lockedClass() { return !this.inside; }
  @HostBinding('class.card--active') get activeClass() { return this.inside; }

  ngAfterViewInit(): void {
    if (this.hostEl.tabIndex < 0) this.hostEl.tabIndex = 0;
    this.lockInner(); 
  }

  ngOnDestroy(): void {
    this.restoreTabindexes();
  }

  private getFocusableInside(): HTMLElement[] {
    const selector = [
      'a[href]',
      'button',
      'input',
      'select',
      'textarea',
      'summary',
      '[role="button"]',
      '[role="link"]',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');
    const list = Array.from(this.hostEl.querySelectorAll<HTMLElement>(selector));
    return list.filter((el) => this.isFocusable(el));
  }

  private isFocusable(el: HTMLElement): boolean {
    if (el === this.hostEl) return false;
    if (!this.hostEl.contains(el)) return false;
    if (el.hasAttribute('disabled')) return false;
    const cs = getComputedStyle(el);
    return cs.visibility !== 'hidden' && cs.display !== 'none';
  }

  private lockInner(): void {
    this.restoreTabindexes();
    const focusables = this.getFocusableInside();
    focusables.forEach((el) => {
      const orig = el.getAttribute('tabindex');
      this.managed.push({ el, orig });
      this.renderer.setAttribute(el, 'tabindex', '-1');
    });
  }

  private unlockInner(): void {
    this.restoreTabindexes();
  }

  private restoreTabindexes(): void {
    this.managed.forEach(({ el, orig }) => {
      if (orig == null) el.removeAttribute('tabindex');
      else this.renderer.setAttribute(el, 'tabindex', String(orig));
    });
    this.managed = [];
  }

  private focusFirstInside(): void {
    if (this.firstFocusSelector) {
      const explicit = this.hostEl.querySelector<HTMLElement>(this.firstFocusSelector);
      if (explicit) { explicit.focus(); return; }
    }
    const first = this.getFocusableInside()[0];
    if (first) first.focus();
  }

  private enter(): void {
    if (this.inside) return;
    this.inside = true;
    this.unlockInner();
    queueMicrotask(() => this.focusFirstInside());
  }

  private exit(): void {
    if (!this.inside) return;
    this.inside = false;
    this.lockInner();
    this.hostEl.focus();
  }

  @HostListener('keydown', ['$event'])
  onKeydown(ev: KeyboardEvent) {
    if (ev.key === 'space' || ev.key === ' ') {
      ev.preventDefault();
      this.enter();
    } else if (ev.key === 'Escape') {
      if (this.inside) {
        ev.preventDefault();
        this.exit();
      }
    }
  }

  @HostListener('focusin', ['$event'])
  onFocusIn(ev: FocusEvent) {
    if (this.inside) return;
    const target = ev.target as HTMLElement | null;
    if (!target || target === this.hostEl) return;
    if (this.suppressFocusBounce) return;
    this.suppressFocusBounce = true;
    this.hostEl.focus();
    queueMicrotask(() => (this.suppressFocusBounce = false));
  }

  @HostListener('focusout', ['$event'])
  onFocusOut(ev: FocusEvent) {
    const next = ev.relatedTarget as Node | null;
    if (this.inside && next && !this.hostEl.contains(next)) {
      this.exit();
    }
  }

}
